import React, { useState, useEffect, useRef } from 'react';

import { FaChevronDown, FaChevronRight, FaCalendarAlt, FaPencilAlt, FaThLarge, FaList, FaCog, FaCheck } from 'react-icons/fa';
import { getBlockers } from '../../../utils/idHelpers';
import { apiFetch } from '../../../utils/apiAdmin';
import { formatRelativeDay, getEffectiveRelativeDateRange } from '../../../utils/templateDateUtils';
import Tooltip from '../../../components/Tooltip';
import EffortInput from '../../../components/EffortInput';
import { formatEffort, sumEffort } from '../../../utils/effort';
import { coreMainTasks, effortLabel, isOverrun, varianceLabel, isClientLaneTask } from '../../../utils/effortDisplay';

// ⛔ ANDROID DRAWS ▶ FROM THE COLOUR EMOJI FONT, SO EVERY COLOUR RULE IS IGNORED
//
// ISS-086 in organise-app, transcribed here 2026-08-30. Bill, on Firefox
// Android: "why on firefox android android are all my arrows on gantt bright
// orange?" - and nothing in the code is orange.
//
// U+25B6 is "text default" in Unicode, but Android's text fonts carry NO GLYPH
// for it, so the fallback chain reaches Noto Color Emoji and draws the
// media-player triangle. A colour-emoji glyph paints its OWN colour, so the
// element's text-* class has nothing to bite on. Firefox Android reaches for
// that font more eagerly than Chrome.
//
// U+FE0E is VARIATION SELECTOR-15: "render the TEXT presentation of the
// preceding character". Zero-width, so no geometry moves; it changes which FONT
// is chosen, which is the whole problem.
//
// ⛔ ONE definition, worn by both sites. A missed site is orange on Android and
// correct everywhere else - invisible to anyone testing on a desktop.
//
// ⚠️ Scope, checked rather than assumed: this is the ONLY affected glyph in the
// portal. The Gantt's ►/▼ (U+25BA/U+25BC) and its ›/‹ column chevrons
// (U+203A/U+2039) have no emoji presentation at all, so they need nothing and a
// blanket sweep would be wrong.
export const COLLAPSE_ARROW = '▶︎';

// Helper to format date as UK format (DD/MM/YYYY)
const formatDateUK = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

// Helper to check if date is overdue
const isOverdue = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
};

// Helper to check if date is within 2 days
const isWithin2Days = (dateString) => {
    if (!dateString) return false;
    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    const diffDays = (dueDate - today) / (1000 * 60 * 60 * 24);
    return diffDays >= 0 && diffDays <= 2;
};

// Helper to get due date background colour class
// Red: overdue, Orange: within 2 days, Grey: more than 2 days away
const getDueDateBgColour = (dateString) => {
    if (!dateString) return '';
    if (isOverdue(dateString)) return 'bg-red-100 text-red-700';
    if (isWithin2Days(dateString)) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700'; // Far in future - not urgent
};

// Helper to get start date background colour class
// Grey: more than 3 days away, Orange: within 3 days, Green: started (today or past)
const getStartDateBgColour = (dateString) => {
    if (!dateString) return 'bg-gray-100 text-gray-500';
    const startDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    const diffDays = (startDate - today) / (1000 * 60 * 60 * 24);

    if (diffDays <= 0) return 'bg-green-100 text-green-700'; // Started (today or past)
    if (diffDays <= 3) return 'bg-orange-100 text-orange-700'; // Within 3 days
    return 'bg-gray-100 text-gray-500'; // More than 3 days away
};

// Calculate effective date range from subtasks
const getEffectiveDateRange = (task, allTasks) => {
    const subtasks = allTasks.filter(t => String(t.mainTaskId) === String(task.taskId) && t.projectId === task.projectId);
    if (subtasks.length === 0) {
        // No subtasks - use task's own dates
        return { start: task.startDate, end: task.dueDate };
    }

    // Find earliest start date from subtasks
    const startDates = subtasks
        .map(st => st.startDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const start = startDates.length > 0
        ? new Date(Math.min(...startDates)).toISOString().split('T')[0]
        : null;

    // Find latest due date from subtasks
    const endDates = subtasks
        .map(st => st.dueDate)
        .filter(Boolean)
        .map(d => new Date(d));
    const end = endDates.length > 0
        ? new Date(Math.max(...endDates)).toISOString().split('T')[0]
        : null;

    return { start, end };
};

// Card drop shadow.
//
// This has to be inline rather than a Tailwind shadow-* class. Cards already
// set an inline boxShadow for the add-in and team-task inset colour bars, and
// an inline box-shadow replaces the class outright - so every add-in and every
// Team-swimlane card was rendering with no drop shadow at all while the plain
// ones had shadow-sm. Composing both into one value is what makes them match.
// Cast down and to the RIGHT, matching the Gantt bars ('2px 3px 6px'), so the
// two views agree on where the light is coming from.
const CARD_SHADOW = '2px 3px 6px rgba(0, 0, 0, 0.11), 1px 1px 2px rgba(0, 0, 0, 0.06)';
const CARD_SHADOW_HOVER = '4px 7px 14px rgba(0, 0, 0, 0.16), 2px 2px 4px rgba(0, 0, 0, 0.08)';

/**
 * Compose a card's inset indicator bars with its drop shadow.
 * @param {string|null} insets  inset box-shadow(s), or null for a plain card
 * @param {boolean} isHovered   lift the card while hovered
 */
const cardBoxShadow = (insets, isHovered) => {
    const drop = isHovered ? CARD_SHADOW_HOVER : CARD_SHADOW;
    return insets ? `${insets}, ${drop}` : drop;
};

// ⛔ THE BOARD BY FINGER, AND IT REUSES THE MOUSE PATH'S OWN COMMIT (Stage E,
// 2026-08-30).
//
// HTML5 drag-and-drop is not driven by touch in iOS Safari, and this board had
// no fallback at all - so on a tablet the cards simply could not be moved.
//
// ⛔ NOT FIXED BY A BREAKPOINT. A long press lifts the card, elementFromPoint
// finds the column, and the drop calls THE SAME TWO PROPS the mouse path calls -
// onDragStart(task) then onDrop(epicId). Everything that makes a drop correct
// lives behind those: the permission checks, the closure-comment prompt, the
// optimistic update and its rollback. A second commit path would have to
// re-implement every one of them, and would drift from the first the moment
// either changed.
//
// ⛔ THE MOUSE KEEPS HTML5 DnD. Two input paths, deliberately.
const LIFT_MS = 350;
// ⛔ LOAD-BEARING, NOT TASTE. The non-passive preventDefault below is only
// honoured while the browser has not STARTED scrolling, so this tolerance has to
// sit under the browser's own scroll-start threshold (~5-15px, unspecified).
const LIFT_SLOP = 5;
// ⚠️ Claude's number. Near either end of the strip, nudge it along.
const EDGE_ZONE = 60;
const EDGE_STEP = 12;

const useCardTouchDrag = ({ onDragStart, onDrop, readOnly }) => {
    const dragRef = useRef(null);
    const [lifted, setLifted] = useState(null);

    const cleanup = () => {
        const d = dragRef.current;
        if (!d) return;
        dragRef.current = null;
        if (d.timer) clearTimeout(d.timer);
        // ⛔ CANCELLED IN THE SAME THREE PLACES THE LIFT IS - drop, cancel and
        // unmount. A leaked rAF goes on scrolling the board by itself, which
        // reads as the page being possessed.
        if (d.frame) cancelAnimationFrame(d.frame);
        if (d.strip && d.onTouchMove) {
            d.strip.removeEventListener('touchmove', d.onTouchMove);
        }
        if (d.onContextMenu) {
            document.removeEventListener('contextmenu', d.onContextMenu);
        }
        document.body.style.webkitTouchCallout = d.priorCallout || '';
        document.body.style.userSelect = d.priorSelect || '';
        setLifted(null);
    };

    useEffect(() => cleanup, []);

    const columnAt = (x, y) => {
        const el = document.elementFromPoint(x, y);
        const column = el && el.closest ? el.closest('[data-epic-id]') : null;
        return column ? column.dataset.epicId : null;
    };

    const onPointerDown = (e, task) => {
        if (readOnly || e.pointerType !== 'touch') return;
        cleanup();
        const strip = e.currentTarget.closest('.overflow-x-auto');
        dragRef.current = {
            task,
            x: e.clientX,
            y: e.clientY,
            armed: false,
            strip,
            timer: setTimeout(() => {
                const d = dragRef.current;
                if (!d) return;
                d.armed = true;

                // Four things break a long press silently, and all four are
                // handled here.
                //
                // 1. The context menu, which a long press also summons.
                d.onContextMenu = (ev) => ev.preventDefault();
                document.addEventListener('contextmenu', d.onContextMenu);

                // 2/3. The callout and the selection, both of which a held
                // finger triggers on iOS.
                d.priorCallout = document.body.style.webkitTouchCallout;
                d.priorSelect = document.body.style.userSelect;
                document.body.style.webkitTouchCallout = 'none';
                document.body.style.userSelect = 'none';

                // 4. ⛔ touch-action CANNOT be switched on now. It is latched
                // when the gesture STARTS, 350ms ago, so setting it here is a
                // no-op: the browser claims the strip's scroll, pointercancel
                // fires, and the drag dies at the exact moment it lifts - the
                // worst failure, because the feedback has already gone off.
                // What works is a non-passive touchmove that prevents.
                if (d.strip) {
                    d.onTouchMove = (ev) => { if (dragRef.current?.armed) ev.preventDefault(); };
                    d.strip.addEventListener('touchmove', d.onTouchMove, { passive: false });
                }

                // ⚠️ Feedback is VISUAL. iOS Safari implements NO Vibration API
                // at all, so a haptic-only signal would be invisible on the very
                // device this was built for.
                setLifted({ task: d.task, x: d.x, y: d.y });
            }, LIFT_MS)
        };
    };

    const onPointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        if (!d.armed) {
            if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > LIFT_SLOP) {
                clearTimeout(d.timer);
                d.timer = null;
                dragRef.current = null;
            }
            return;
        }
        d.x = e.clientX;
        d.y = e.clientY;
        setLifted({ task: d.task, x: e.clientX, y: e.clientY });

        // ⛔ THE PREVENTED SCROLL IS GIVEN BACK AS EDGE AUTO-SCROLL. The
        // preventDefault above kills the strip's own scrolling for the WHOLE
        // drag, and the strip is overflow-x-auto - so on a board wider than the
        // screen some columns would be unreachable while a card is held. The
        // mouse path never had this problem: HTML5 DnD gets the browser's own
        // drag auto-scroll for free.
        if (!d.strip) return;
        const box = d.strip.getBoundingClientRect();
        const dir = e.clientX < box.left + EDGE_ZONE ? -1
            : e.clientX > box.right - EDGE_ZONE ? 1 : 0;
        if (dir === 0) {
            if (d.frame) { cancelAnimationFrame(d.frame); d.frame = null; }
            return;
        }
        if (d.frame) return;
        const step = () => {
            const live = dragRef.current;
            if (!live || !live.armed || !live.strip) return;
            live.strip.scrollLeft += dir * EDGE_STEP;
            live.frame = requestAnimationFrame(step);
        };
        d.frame = requestAnimationFrame(step);
    };

    const onPointerUp = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const armed = d.armed;
        const task = d.task;
        // ⛔ elementFromPoint is read HERE, at the release, and per frame while
        // auto-scrolling - the columns move under a stationary finger, so a
        // target resolved only on movement would go stale precisely while the
        // chart is doing the moving.
        const epicId = armed ? columnAt(e.clientX, e.clientY) : null;
        cleanup();
        if (!armed || epicId === null) return;
        // The mouse path's own two calls, in its own order.
        onDragStart(task);
        onDrop(epicId);
    };

    return {
        lifted,
        cardHandlers: (task) => ({
            onPointerDown: (e) => onPointerDown(e, task),
            onPointerMove,
            onPointerUp,
            onPointerCancel: cleanup
        })
    };
};

const KanbanBoard = ({
    project,
    tasks,
    columns,
    members,
    collapsedColumns,
    setCollapsedColumns,
    collapsedSwimlanes = new Set(),
    setCollapsedSwimlanes = () => {},
    hoveredMainTaskId,
    setHoveredMainTaskId = () => {},
    teamSwimlaneId,
    draggedTask,
    onDragStart,
    onDragOver,
    onDrop,
    onTaskClick,
    onCreateTaskClick,
    isAdmin,
    isAdminPortal = false,
    lead,
    readOnly = false,
    edgeColumnsInitialised = true,
    blockedTasksMap = new Map(),
    isTemplateMode = false,
    onEpicChanged = null
}) => {
    // The board by finger. ⛔ Its drop calls onDragStart/onDrop - the SAME two
    // props the mouse path calls - so permissions, the closure-comment prompt
    // and the rollback are all shared rather than re-implemented.
    const { lifted, cardHandlers } = useCardTouchDrag({ onDragStart, onDrop, readOnly });

    // Compact view state per swimlane (persisted to localStorage)
    const [compactSwimlanes, setCompactSwimlanes] = useState(() => {
        const saved = localStorage.getItem('kanban-compact-swimlanes');
        return saved ? JSON.parse(saved) : {};
    });

    // Persist compact view preference per swimlane
    useEffect(() => {
        localStorage.setItem('kanban-compact-swimlanes', JSON.stringify(compactSwimlanes));
    }, [compactSwimlanes]);

    // Swimlane height split (draggable divider). One global key: unlikely to
    // have two projects open, and every other view preference here is
    // browser-local too.
    const lanesRef = useRef(null);
    const [laneSplit, setLaneSplit] = useState(() => {
        const saved = parseFloat(localStorage.getItem('kanbanSwimlaneSplit'));
        return Number.isFinite(saved) ? Math.min(0.85, Math.max(0.15, saved)) : 0.5;
    });

    // Listeners go on window, not the 6px strip, or the drag dies the moment the
    // cursor outruns it. Measured against the lanes' own flex column - the window
    // or the scroll container would give a drag that tracks at the wrong rate.
    const startLaneDrag = (e) => {
        e.preventDefault();
        const container = lanesRef.current;
        if (!container) return;
        const height = container.getBoundingClientRect().height;
        if (height <= 0) return;
        const startY = e.clientY;
        const startSplit = laneSplit;
        const onMove = (ev) => {
            const next = startSplit + (ev.clientY - startY) / height;
            setLaneSplit(Math.min(0.85, Math.max(0.15, next)));
        };
        const onUp = (ev) => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            const final = Math.min(0.85, Math.max(0.15, startSplit + (ev.clientY - startY) / height));
            localStorage.setItem('kanbanSwimlaneSplit', String(final));
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    const resetLaneSplit = () => {
        setLaneSplit(0.5);
        localStorage.setItem('kanbanSwimlaneSplit', '0.5');
    };

    // Helper to check if a swimlane is in compact mode
    const isCompact = (swimlaneKey) => compactSwimlanes[swimlaneKey] === true;

    // Toggle compact mode for a swimlane
    const toggleCompact = (swimlaneKey) => {
        setCompactSwimlanes(prev => ({
            ...prev,
            [swimlaneKey]: !prev[swimlaneKey]
        }));
    };

    // Hide-completed state per swimlane (persisted to localStorage), mirroring
    // the compact-view pattern above. Defaults to showing completed tasks.
    const [hideCompletedSwimlanes, setHideCompletedSwimlanes] = useState(() => {
        const saved = localStorage.getItem('kanbanHideCompleted');
        return saved ? JSON.parse(saved) : {};
    });

    useEffect(() => {
        localStorage.setItem('kanbanHideCompleted', JSON.stringify(hideCompletedSwimlanes));
    }, [hideCompletedSwimlanes]);

    // Helper to check if a swimlane is hiding completed main tasks
    const isHidingCompleted = (swimlaneKey) => hideCompletedSwimlanes[swimlaneKey] === true;

    // Toggle hide-completed for a swimlane
    const toggleHideCompleted = (swimlaneKey) => {
        setHideCompletedSwimlanes(prev => ({
            ...prev,
            [swimlaneKey]: !prev[swimlaneKey]
        }));
    };

    // Epic rename state (admin only)
    const [editingEpicId, setEditingEpicId] = useState(null);
    const [editingEpicName, setEditingEpicName] = useState('');
    const [savingEpic, setSavingEpic] = useState(false);

    // Epic management state (template mode only)
    const [epicDropdownId, setEpicDropdownId] = useState(null);
    const [addColumnPrompt, setAddColumnPrompt] = useState(null);
    const [addColumnName, setAddColumnName] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [epicActionLoading, setEpicActionLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Epic effort estimate (T470/D19). ADMIN ONLY - the client portal never
    // renders any of it, and the server strips the field from client reads.
    const [effortEpicId, setEffortEpicId] = useState(null);
    const [savingEpicEffort, setSavingEpicEffort] = useState(false);

    // Handle epic rename save
    const handleEpicSave = async (e) => {
        e.preventDefault();
        if (!editingEpicName.trim() || savingEpic) return;

        setSavingEpic(true);
        try {
            await apiFetch(`${process.env.REACT_APP_API_URL}/admin/epic/${project.projectId}/${editingEpicId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: editingEpicName.trim() })
            });
            setEditingEpicId(null);
            setEditingEpicName('');
            if (onEpicChanged) onEpicChanged();
        } catch (err) {
            console.error('Failed to rename epic:', err);
        } finally {
            setSavingEpic(false);
        }
    };

    const startEpicEdit = (column) => {
        setEditingEpicId(column.epicId);
        setEditingEpicName(column.name);
    };

    /**
     * Save an epic's typed effort estimate (T470/D19).
     *
     * The SAME route the rename uses - `updateEpic` in the layer accepts either
     * field, which is why T464 relaxed its "name is required" refusal.
     */
    const handleEpicEffortSave = async (epicId, value) => {
        setSavingEpicEffort(true);
        try {
            await apiFetch(`${process.env.REACT_APP_API_URL}/admin/epic/${project.projectId}/${epicId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ effortEstimated: value })
            });
            setEffortEpicId(null);
            if (onEpicChanged) onEpicChanged();
        } catch (err) {
            console.error('Failed to save epic effort:', err);
        } finally {
            setSavingEpicEffort(false);
        }
    };

    /**
     * The epic's effort line: the typed estimate, the two rollups, the variance
     * and the unestimated count (D7/D24/D6).
     *
     * ⛔ THE ROLLUPS USE `coreMainTasks`, the one shared selector - not
     * `columnTasks`, which is filtered for display. A total that changed with a
     * filter would be a different number on two screens showing the same epic.
     * It is passed the client lane so its own work is excluded (D1, ECL-01).
     *
     * Returns null in the client portal and for a client-facing render, so the
     * numbers cannot appear even if a value reached the browser.
     *
     * ⚠️ ONE COG PER HEADER (Bill, 2026-09-01: "I can't have two cogs"). In
     * template mode the trigger is a "Set effort…" item on the EXISTING column
     * options menu - the D19 decision as originally taken - so the inline cog
     * below renders in the ADMIN CLIENT VIEW only, where no menu exists.
     *
     * `editorHere` gates WHERE the opened editor appears: a column spans every
     * lane and the header renders per lane, but unlike the rename form (shared
     * controlled state, safe to duplicate) EffortInput holds its OWN draft -
     * two copies would be two half-filled inputs. Template mode passes it for
     * the first lane only, beside the menu that opened it.
     */
    const renderEpicEffort = (column, { editorHere = true, swimlane = null } = {}) => {
        if (!(isAdminPortal || isTemplateMode)) return null;
        // D3 (Bill, 2026-09-02): the client lane's copy of this header shows no
        // effort line. Nothing under it feeds these numbers, and a total printed
        // above the wrong cards reads as that lane's total.
        if (swimlane && isClientLaneTask({ swimlaneId: swimlane.swimlaneId }, teamSwimlaneId, project)) {
            return null;
        }

        const rows = coreMainTasks(tasks, { epicId: column.epicId, teamSwimlaneId, project });
        const estimates = sumEffort(rows, 'effortEstimated');
        const actuals = sumEffort(rows, 'effortActual');
        const typed = column.effortEstimated;
        // D5: a template is never worked on, so it shows no actual and no
        // variance - there is nothing to be over.
        const showActual = !isTemplateMode;

        return (
            // ⚠️ Own line UNDER the title (Bill, 2026-09-01), and flex-wrap so a
            // long line - or the open EffortInput - wraps instead of clipping
            // out of view, which is what made the control look dead.
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px] font-normal normal-case text-gray-500">
                <span title="Estimate typed on this column">
                    Est {formatEffort(typed) ?? '-'}
                </span>
                <span className="text-gray-300">·</span>
                <span title="Sum of this column's main-task estimates">
                    Planned {formatEffort(estimates.total)}
                </span>
                {showActual && (
                    <>
                    <span className="text-gray-300">·</span>
                    <span title="Sum of this column's main-task actuals">
                        Act {formatEffort(actuals.total)}
                    </span>
                    <span className="text-gray-300">·</span>
                    <span
                        className={isOverrun(typed, actuals.total) ? 'text-red-600 font-semibold' : ''}
                        title="Actual effort minus the estimate typed on this column"
                    >
                        {varianceLabel(typed, actuals.total)}
                    </span>
                    </>
                )}
                {estimates.unestimated > 0 && (
                    <>
                    <span className="text-gray-300">·</span>
                    <span
                        className="text-amber-600"
                        title="Main tasks in this column with no estimate. The Planned total does not include them."
                    >
                        {estimates.unestimated} unestimated
                    </span>
                    </>
                )}
                {isAdminPortal && !isTemplateMode && !readOnly && (
                    <Tooltip text="Set this column's effort estimate">
                        <FaCog
                            className="w-3 h-3 text-gray-400 hover:text-primary-600 cursor-pointer"
                            onClick={(e) => {
                                e.stopPropagation();
                                setEffortEpicId(effortEpicId === column.epicId ? null : column.epicId);
                            }}
                            aria-label={`Set effort for ${column.name}`}
                        />
                    </Tooltip>
                )}
                {editorHere && effortEpicId === column.epicId && (
                    <span onClick={e => e.stopPropagation()}>
                        <EffortInput
                            value={typed}
                            saving={savingEpicEffort}
                            onCommit={val => handleEpicEffortSave(column.epicId, val)}
                        />
                    </span>
                )}
            </div>
        );
    };

    const cancelEpicEdit = () => {
        setEditingEpicId(null);
        setEditingEpicName('');
    };

    // Close epic dropdown on outside click
    useEffect(() => {
        if (!epicDropdownId) return;
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setEpicDropdownId(null);
            }
        };
        const handleEscape = (e) => {
            if (e.key === 'Escape') setEpicDropdownId(null);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, [epicDropdownId]);

    // Handle add column submit
    const handleAddColumn = async () => {
        if (!addColumnName.trim() || epicActionLoading || !addColumnPrompt) return;
        setEpicActionLoading(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/epic/${project.projectId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: addColumnName.trim(),
                    referenceEpicId: addColumnPrompt.referenceEpicId,
                    position: addColumnPrompt.position
                })
            });
            if (!response.ok) {
                const data = await response.json();
                alert(data.error || 'Failed to add column');
                return;
            }
            setAddColumnPrompt(null);
            setAddColumnName('');
            if (onEpicChanged) onEpicChanged();
        } catch (err) {
            alert(err.message || 'Failed to add column');
        } finally {
            setEpicActionLoading(false);
        }
    };

    // Handle delete column
    const handleDeleteColumn = async () => {
        if (!deleteConfirm || epicActionLoading) return;
        setEpicActionLoading(true);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/epic/${project.projectId}/${deleteConfirm.epicId}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                const data = await response.json();
                alert(data.error || 'Failed to delete column');
                setDeleteConfirm(null);
                return;
            }
            setDeleteConfirm(null);
            if (onEpicChanged) onEpicChanged();
        } catch (err) {
            alert(err.message || 'Failed to delete column');
            setDeleteConfirm(null);
        } finally {
            setEpicActionLoading(false);
        }
    };

    // Check if column should be collapsed (including Edge columns before initialisation)
    const isColumnCollapsed = (columnName) => {
        if (collapsedColumns.has(columnName)) return true;
        if (!edgeColumnsInitialised && columnName.startsWith('Edge')) return true;
        return false;
    };
    const toggleSwimlaneCollapse = (swimlaneId) => {
        const newCollapsed = new Set(collapsedSwimlanes);
        if (newCollapsed.has(swimlaneId)) {
            newCollapsed.delete(swimlaneId);
        } else {
            newCollapsed.add(swimlaneId);
        }
        setCollapsedSwimlanes(newCollapsed);
    };
    // Check if project has swimlanes
    if (!project?.swimlanes || project.swimlanes.length === 0) {
        // Fallback: Original single-board view (no swimlanes) - use 'fallback' as swimlane key
        const fallbackKey = 'fallback';
        const compactView = isCompact(fallbackKey);
        const hidingCompleted = !isTemplateMode && isHidingCompleted(fallbackKey);
        return (
            <div className="flex flex-col">
            <div className="flex justify-end mb-2 gap-2">
                {!isTemplateMode && (
                    <Tooltip text={hidingCompleted ? 'Show completed main tasks' : 'Hide completed main tasks'}>
                    <button
                        onClick={() => toggleHideCompleted(fallbackKey)}
                        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded border transition-colors ${
                            hidingCompleted
                                ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                                : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                        }`}
                    >
                        <FaCheck className="w-3.5 h-3.5" />
                        <span>Hide completed</span>
                    </button>
                    </Tooltip>
                )}
                <Tooltip text={compactView ? 'Switch to detailed view' : 'Switch to compact view'}>
                <button
                    onClick={() => toggleCompact(fallbackKey)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-colors"
                >
                    {compactView ? (
                        <>
                            <FaThLarge className="w-3.5 h-3.5" />
                            <span>Detailed</span>
                        </>
                    ) : (
                        <>
                            <FaList className="w-3.5 h-3.5" />
                            <span>Compact</span>
                        </>
                    )}
                </button>
                </Tooltip>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 justify-center">
            {columns.map(column => {
                const columnTasks = tasks.filter(task => {
                    // Only show main tasks on the kanban board (not subtasks)
                    if (task.mainTaskId) return false;

                    // Completed main tasks drop out when the toggle is on
                    if (hidingCompleted && task.isClosed === true) return false;

                    // Main tasks use epicId for kanban column
                    if (String(task.epicId) === String(column.epicId)) return true;

                    return false;
                });

                const isCollapsed = isColumnCollapsed(column.name);

                return (
                    <div
                    key={column.epicId}
                    className={`flex-shrink-0 ${isCollapsed ? 'w-12' : 'w-80'} transition-all duration-300`}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(column.epicId)}
                    // ⛔ How the TOUCH path finds this column. elementFromPoint
                    // returns whatever is under the finger - a card, a label, a
                    // badge - so the drop target is identified by walking up to
                    // this attribute rather than by guessing at the element.
                    data-epic-id={column.epicId}
                    >
                    {isCollapsed ? (
                        <div
                        className="bg-white rounded-lg h-[500px] flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-300 transition-colors relative group"
                        onClick={() => {
                            const newCollapsed = new Set(collapsedColumns);
                            newCollapsed.delete(column.name);
                            setCollapsedColumns(newCollapsed);
                        }}
                        >
                        <div className="absolute top-4 text-gray-400 group-hover:text-primary-600">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                        </svg>
                        </div>
                        <div className="transform -rotate-90 whitespace-nowrap">
                        <span className="font-semibold text-gray-800 uppercase text-sm">
                        {column.name} ({columnTasks.length})
                        </span>
                        </div>
                        </div>
                    ) : (
                        <>
                        <div className="bg-white rounded-t-lg px-4 py-3 border-b-2 border-gray-200">
                        <div className="flex justify-between items-center">
                        {editingEpicId === column.epicId ? (
                            <form onSubmit={handleEpicSave} className="flex items-center gap-1 flex-1">
                            <input
                                type="text"
                                value={editingEpicName}
                                onChange={(e) => setEditingEpicName(e.target.value)}
                                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 flex-1"
                                autoFocus
                                onKeyDown={(e) => e.key === 'Escape' && cancelEpicEdit()}
                                disabled={savingEpic}
                            />
                            <button
                                type="submit"
                                disabled={savingEpic}
                                className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                            >
                                {savingEpic ? '...' : 'Save'}
                            </button>
                            <button
                                type="button"
                                onClick={cancelEpicEdit}
                                disabled={savingEpic}
                                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            </form>
                        ) : (
                            <>
                            <h3 className="font-semibold text-gray-800 uppercase text-sm">
                            {column.name}
                            </h3>
                            <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                            {columnTasks.length}
                            </span>
                            </>
                        )}
                        </div>
                        {/* T470 - BOTH header paths carry it, or the numbers
                            vanish in one of the two layouts. ⚠️ ON ITS OWN LINE
                            UNDER THE TITLE (Bill, 2026-09-01): inline on the
                            title row there was no room, so the EffortInput
                            opened clipped out of view and could not be typed
                            in. */}
                        {renderEpicEffort(column)}
                        </div>

                        <div className="bg-gray-100 rounded-b-lg p-4 min-h-[500px] space-y-3">
                        {columnTasks.length === 0 ? (
                            <div className="text-center text-gray-400 text-sm py-8">
                            No tasks
                            </div>
                        ) : (
                            [...columnTasks].sort((a, b) => {
                                // In template mode, use combinedMainTaskOrder from project
                                if (isTemplateMode && project?.combinedMainTaskOrder) {
                                    const aKey = `${a._sourceTemplateId || a.projectId}:${a.taskId}`;
                                    const bKey = `${b._sourceTemplateId || b.projectId}:${b.taskId}`;
                                    const aOrder = project.combinedMainTaskOrder[aKey] ?? 9999;
                                    const bOrder = project.combinedMainTaskOrder[bKey] ?? 9999;
                                    return aOrder - bOrder;
                                }
                                // Default: team tasks at bottom, then by start date
                                const aIsTeam = a.swimlaneId === teamSwimlaneId || (a.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);
                                const bIsTeam = b.swimlaneId === teamSwimlaneId || (b.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);
                                if (aIsTeam && !bIsTeam) return 1;
                                if (!aIsTeam && bIsTeam) return -1;
                                const aSubtasks = tasks.filter(t => t.mainTaskId === a.taskId && t.projectId === a.projectId);
                                const bSubtasks = tasks.filter(t => t.mainTaskId === b.taskId && t.projectId === b.projectId);
                                const aStart = aSubtasks.map(st => st.startDate).filter(Boolean).sort()[0] || '9999-12-31';
                                const bStart = bSubtasks.map(st => st.startDate).filter(Boolean).sort()[0] || '9999-12-31';
                                return aStart.localeCompare(bStart);
                            }).map(task => {
                                const isTeamTask = task.swimlaneId === teamSwimlaneId || (task.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);
                                return (
                                <div
                                key={task._sourceTemplateId ? `${task._sourceTemplateId}:${task.taskId}` : task.taskId}
                                draggable={!readOnly}
                                onDragStart={() => !readOnly && onDragStart(task)}
                                {...cardHandlers(task)}
                                onClick={() => onTaskClick(isTemplateMode && task._sourceTemplateId ? `${task._sourceTemplateId}:${task.taskId}` : task.taskId)}
                                onMouseEnter={() => setHoveredMainTaskId(task.taskId)}
                                onMouseLeave={() => setHoveredMainTaskId(null)}
                                className={`bg-white rounded-lg ${compactView ? 'p-2' : 'p-4'} transition-all cursor-pointer border ${
                                    // Same single width as the swimlane path below
                                    hoveredMainTaskId === task.taskId
                                    ? 'border-yellow-400 bg-yellow-50'
                                    : 'border-gray-200'
                                }`}
                                style={{
                                    boxShadow: cardBoxShadow(
                                        task._isAddin && task._isTeamSwimlane
                                            ? `inset 3px 0 0 0 #3b82f6, inset -3px 0 0 0 ${task._addinColour}`
                                            : task._isAddin
                                            ? `inset 3px 0 0 0 ${task._addinColour}`
                                            : isTeamTask
                                            ? 'inset 3px 0 0 0 #3b82f6'
                                            : null,
                                        hoveredMainTaskId === task.taskId
                                    )
                                }}
                                >
                                {compactView ? (
                                    /* Compact view - just the title */
                                    <div className="flex items-center gap-2">
                                        {task._isAddin && (
                                            <span
                                                className="px-1 py-0.5 text-xs font-bold rounded flex-shrink-0"
                                                style={{
                                                    backgroundColor: `${task._addinColour}20`,
                                                    color: task._addinColour
                                                }}
                                            >
                                                {task._addinAbbreviation}
                                            </span>
                                        )}
                                        <h4 className={`font-medium text-sm truncate flex-1 ${task.isClosed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                            {task.isClosed && <FaCheck className="inline-block text-green-600 mr-1 flex-shrink-0" size={10} />}
                                            {task.subject}
                                        </h4>
                                    </div>
                                ) : (
                                    /* Detailed view - full content */
                                    <>
                                    <div className="flex items-start justify-between mb-2">
                                    {/* Add-in badge */}
                                    {task._isAddin && (
                                        <span
                                            className="px-1.5 py-0.5 text-xs font-bold rounded mr-2 flex-shrink-0"
                                            style={{
                                                backgroundColor: `${task._addinColour}20`,
                                                color: task._addinColour
                                            }}
                                        >
                                            {task._addinAbbreviation}
                                        </span>
                                    )}
                                    <h4 className={`font-medium flex-1 pr-2 ${task.isClosed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                    {task.isClosed && <FaCheck className="inline-block text-green-600 mr-1 flex-shrink-0" size={11} />}
                                    {task.subject}
                                    </h4>
                                    <span className="text-xs text-gray-500">#{task.taskId}</span>
                                    </div>
                                    {task.description && (
                                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                        {task.description}
                                        </p>
                                    )}
                                    {/* Effort badge (T472). ADMIN ONLY (D4), and
                                        absent entirely when neither figure is set,
                                        so unestimated cards stay clean. NOT on the
                                        client's own lane (D1, ECL-05). */}
                                    {(isAdminPortal || isTemplateMode)
                                        && !isClientLaneTask(task, teamSwimlaneId, project)
                                        && effortLabel(
                                        task.effortEstimated, task.effortActual,
                                        { estimateOnly: isTemplateMode }
                                    ) && (
                                        <div className="mb-2">
                                        <span
                                            className={`inline-flex items-center px-2 py-0.5 text-xs rounded bg-gray-100 ${
                                                isOverrun(task.effortEstimated, task.effortActual)
                                                    ? 'text-red-600 font-semibold' : 'text-gray-700'
                                            }`}
                                            title="Estimated and actual effort, in days"
                                        >
                                            {effortLabel(
                                                task.effortEstimated, task.effortActual,
                                                { estimateOnly: isTemplateMode }
                                            )}
                                        </span>
                                        </div>
                                    )}
                                    {/* Date range on own row */}
                                    {(() => {
                                        const { start, end } = isTemplateMode
                                            ? getEffectiveRelativeDateRange(task, tasks)
                                            : getEffectiveDateRange(task, tasks);
                                        if (!start && !end) return null;
                                        return (
                                            <div className="mb-2 flex items-center gap-2">
                                            {start && (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${isTemplateMode ? 'bg-gray-100 text-gray-700' : getStartDateBgColour(start)}`}>
                                                <FaCalendarAlt className="w-3 h-3" />
                                                {isTemplateMode ? formatRelativeDay(start) : formatDateUK(start)}
                                                </span>
                                            )}
                                            {start && end && <span className={"text-xs text-gray-600"}>{'\u2192'}</span>}
                                            {end && (
                                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${isTemplateMode ? 'bg-gray-100 text-gray-700' : getDueDateBgColour(end)}`}>
                                                <FaCalendarAlt className="w-3 h-3" />
                                                {isTemplateMode ? formatRelativeDay(end) : formatDateUK(end)}
                                                </span>
                                            )}
                                            </div>
                                        );
                                    })()}
                                    <div className="flex items-start justify-between text-xs mt-2">
                                    <div className="flex flex-col gap-1">
                                    {/* No assignee, no name (Bill, 2026-08-03). This used
                                        to fall back to assignedToName, which on a main
                                        task is the subtask field - so a cleared task kept
                                        showing a stale name while staying locked. */}
                                    {task.assignedUsersNames && task.assignedUsersNames.length > 0 ? (
                                        // Show all assignee names
                                        task.assignedUsersNames.map((name, idx) => (
                                            <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs whitespace-nowrap">
                                            {name}
                                            </span>
                                        ))
                                    ) : null}
                                    </div>

                                    <div className="flex items-center gap-2 flex-shrink-0">
                                    {(task.activeCommentsCount > 0 || (task.activeCommentsCount === undefined && task.totalComments > 0)) && (
                                        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                        {task.activeCommentsCount !== undefined ? task.activeCommentsCount : task.totalComments}
                                        </span>
                                    )}
                                    {/* Show subtask count with "yours" indicator */}
                                    {(() => {
                                        const subtaskCount = tasks.filter(t => t.mainTaskId === task.taskId && t.projectId === task.projectId).length;
                                        if (subtaskCount === 0) return null;

                                        // Get current user ID for assignment check
                                        const userId = lead?.id;
                                        const userEmail = (lead?.email || '').toLowerCase();
                                        const matchesMe = (v) => v != null && (String(v) === String(userId) || (userEmail && String(v).toLowerCase() === userEmail));
                                        const mySubtasks = tasks.filter(t =>
                                            String(t.mainTaskId) === String(task.taskId) &&
                                            t.projectId === task.projectId &&
                                            (matchesMe(t.assignedTo) ||
                                            t.assignedUsers?.some(u => matchesMe(u)))
                                        ).length;

                                        return (
                                            <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1">
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                            </svg>
                                            {subtaskCount}{mySubtasks > 0 && <span className="text-primary-600">({mySubtasks} yours)</span>}
                                            </span>
                                        );
                                    })()}
                                    </div>
                                    </div>
                                    </>
                                )}
                                </div>
                            );
                            })
                        )}

                        {(column.name === 'Archived' || column.name.startsWith('Edge')) && (
                            <div className="text-center pt-2 border-t border-gray-300 mt-2">
                            <button
                            onClick={(e) => {
                                e.stopPropagation();
                                const newCollapsed = new Set(collapsedColumns);
                                newCollapsed.add(column.name);
                                setCollapsedColumns(newCollapsed);
                            }}
                            className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                            >
                            Collapse {COLLAPSE_ARROW}
                            </button>
                            </div>
                        )}
                        </div>
                        </>
                    )}
                    </div>
                );
            })}
            </div>
            </div>
        );
    }

    // Swimlane-based view
    const sortedSwimlanes = [...project.swimlanes].sort((a, b) => a.order - b.order);
    // ⚠️ NO `|| sortedSwimlanes[0]` FALLBACK. `CLAUDE.md` forbids adding a
    // fallback without asking, and this one would hide its own case: if it ever
    // fired, EVERY lane would be the client's, which cannot happen while exactly
    // one lane is named `Team`. Undefined then matches no lane, the editor
    // renders nowhere, and that is a visible fault rather than a silent one on a
    // board that should not exist.
    const firstNonClientLane = sortedSwimlanes.find(
        s => !isClientLaneTask({ swimlaneId: s.swimlaneId }, teamSwimlaneId, project)
    );
    const collapsedCount = sortedSwimlanes.filter(s => collapsedSwimlanes.has(s.swimlaneId)).length;

    // Dynamic grid: collapsed lanes get auto height, expanded lanes share remaining space
    const getGridRows = () => {
        if (collapsedCount === 0) return 'grid-rows-2'; // Both expanded: 50/50
        if (collapsedCount === 2) return ''; // Both collapsed: auto sizing
        return ''; // One collapsed: use flex instead for dynamic sizing
    };

    return (
        <>
        {/* The lifted card, following the finger.
            ⛔ pointer-events-none is REQUIRED, not cosmetic: without it
            elementFromPoint returns THIS clone on every move and at the release,
            never the column underneath, so every drop resolves to nothing.
            ⚠️ And it is the whole feedback - iOS Safari implements no Vibration
            API, so a haptic-only signal would be invisible here. */}
        {lifted && (
            <div
                data-testid="kanban-touch-clone"
                className="fixed z-[200] pointer-events-none w-72 rounded-lg bg-white px-3 py-2 text-sm shadow-2xl ring-2 ring-primary-400 opacity-90"
                style={{ left: lifted.x - 140, top: lifted.y - 24 }}
            >
                {lifted.task.subject}
            </div>
        )}
        <div ref={lanesRef} className={`relative flex flex-col gap-4 h-full ${getGridRows()}`}>
        {sortedSwimlanes.length === 2 && collapsedCount === 0 && (
            // The splitter positions itself, so the placement moves to the
            // tooltip wrapper - see Tooltip's `style` note.
            <Tooltip
                text="Drag to resize, double-click to reset"
                fixed
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: `calc(${laneSplit * 100}% - 3px)`,
                    height: '6px',
                    zIndex: 20
                }}
            >
                <div
                    onMouseDown={startLaneDrag}
                    onDoubleClick={resetLaneSplit}
                    aria-label="Drag to resize, double-click to reset"
                    style={{ width: '100%', height: '100%', cursor: 'row-resize' }}
                />
            </Tooltip>
        )}
        {sortedSwimlanes.map((swimlane, laneIdx) => {
            // Get tasks for this swimlane
            // Feeds both the header count and every column below, so filtering
            // completed tasks here makes both follow the toggle
            const swimlaneTasks = tasks.filter(task => {
                // Exclude subtasks from kanban
                if (task.mainTaskId) return false;

                // Completed main tasks drop out when this swimlane's toggle is on.
                // swimlane.swimlaneId, not swimlaneKey - that const is declared below this filter.
                if (!isTemplateMode && isHidingCompleted(swimlane.swimlaneId) && task.isClosed === true) return false;

                // Task belongs to this swimlane, or is null and this is the default swimlane
                const swimlaneIdField = swimlane.swimlaneId;
                return String(task.swimlaneId) === String(swimlaneIdField) ||
                (task.swimlaneId === null && String(swimlaneIdField) === String(project.default_swimlane));
            });

            const isCollapsed = collapsedSwimlanes.has(swimlane.swimlaneId);

            const swimlaneKey = swimlane.swimlaneId;

            return (
                <div
                key={swimlaneKey}
                className={`bg-white rounded-lg shadow flex flex-col overflow-hidden ${isCollapsed ? '' : 'min-h-0'} ${isCollapsed || !(sortedSwimlanes.length === 2 && collapsedCount === 0) ? (isCollapsed ? '' : 'flex-1') : ''}`}
                style={sortedSwimlanes.length === 2 && collapsedCount === 0 && !isCollapsed
                    ? { flexGrow: laneIdx === 0 ? laneSplit : 1 - laneSplit, flexBasis: 0 }
                    : undefined}
                >
                {/* Swimlane Header */}
                <div
                className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center cursor-pointer hover:bg-gray-100 transition-colors"
                onClick={() => toggleSwimlaneCollapse(swimlaneKey)}
                >
                <div className="flex items-center gap-2">
                {isCollapsed ? <FaChevronRight className="text-gray-500" /> : <FaChevronDown className="text-gray-500" />}
                <h3 className="text-sm font-semibold text-gray-700">
                {swimlane.name === 'Team' ? (
                    isTemplateMode ? 'Client Team' : <>{project?.name}'s Team {!readOnly && !isAdminPortal && lead?.email && <span className="font-normal text-gray-500">- Currently logged in as: {lead?.email}</span>}</>
                ) : swimlane.name}
                </h3>
                <span className="text-xs text-gray-500">({swimlaneTasks.length} Main Tasks)</span>
                </div>
                <div className="flex items-center gap-2">
                {/* Hide completed - sits immediately left of Compact */}
                {!isTemplateMode && (
                    <Tooltip text={isHidingCompleted(swimlaneKey) ? 'Show completed main tasks' : 'Hide completed main tasks'}>
                    <button
                        onClick={(e) => { e.stopPropagation(); toggleHideCompleted(swimlaneKey); }}
                        className={`flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border transition-colors ${
                            isHidingCompleted(swimlaneKey)
                                ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                                : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-600'
                        }`}
                    >
                        <FaCheck className="w-3 h-3" />
                        <span>Hide completed</span>
                    </button>
                    </Tooltip>
                )}
                {/* Compact/Detailed toggle */}
                <Tooltip text={isCompact(swimlaneKey) ? 'Switch to detailed view' : 'Switch to compact view'}>
                <button
                    onClick={(e) => { e.stopPropagation(); toggleCompact(swimlaneKey); }}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
                >
                    {isCompact(swimlaneKey) ? (
                        <>
                            <FaThLarge className="w-3 h-3" />
                            <span>Detailed</span>
                        </>
                    ) : (
                        <>
                            <FaList className="w-3 h-3" />
                            <span>Compact</span>
                        </>
                    )}
                </button>
                </Tooltip>
                {((swimlane.name === 'Team' && isAdmin) || isAdminPortal) && !readOnly && (
                    <button
                    onClick={(e) => { e.stopPropagation(); onCreateTaskClick(swimlaneKey); }}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded hover:bg-primary-700 transition-colors"
                    >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Create Task
                    </button>
                )}
                </div>
                </div>

                {/* Kanban Columns for this swimlane - only show if not collapsed */}
                {!isCollapsed && (
                <div className="p-4 flex-1 overflow-hidden">
                <div className="flex gap-4 overflow-x-auto h-full">
                {columns.map(column => {
                    const columnTasks = swimlaneTasks.filter(task => {
                        // Main tasks use epicId for kanban column
                        if (String(task.epicId) === String(column.epicId)) return true;
                        return false;
                    });

                    const isCollapsed = isColumnCollapsed(column.name);

                    return (
                        <div
                        key={column.epicId}
                        className={`flex-shrink-0 ${isCollapsed ? 'w-12' : 'w-80'} transition-all duration-300 h-full flex flex-col`}
                        onDragOver={onDragOver}
                        onDrop={() => onDrop(column.epicId)}
                        data-epic-id={column.epicId}
                        >
                        {isCollapsed ? (
                            <div
                            className="bg-white rounded-lg h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 border-2 border-gray-200 hover:border-primary-300 transition-colors relative group"
                            onClick={() => {
                                const newCollapsed = new Set(collapsedColumns);
                                newCollapsed.delete(column.name);
                                setCollapsedColumns(newCollapsed);
                            }}
                            >
                            <div className="absolute top-4 text-gray-400 group-hover:text-primary-600">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                            </svg>
                            </div>
                            <div className="transform -rotate-90 whitespace-nowrap">
                            <span className="font-semibold text-gray-800 uppercase text-sm">
                            {column.name} ({columnTasks.length})
                            </span>
                            </div>
                            </div>
                        ) : (
                            <>
                            {/* Column Header */}
                            <div className="bg-white rounded-t-lg px-4 py-3 border-b-2 border-gray-200">
                            <div className="flex justify-between items-center">
                            {editingEpicId === column.epicId ? (
                                <form onSubmit={handleEpicSave} className="flex items-center gap-1 flex-1">
                                <input
                                    type="text"
                                    value={editingEpicName}
                                    onChange={(e) => setEditingEpicName(e.target.value)}
                                    className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 flex-1"
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Escape' && cancelEpicEdit()}
                                    disabled={savingEpic}
                                />
                                <button
                                    type="submit"
                                    disabled={savingEpic}
                                    className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                                >
                                    {savingEpic ? '...' : 'Save'}
                                </button>
                                <button
                                    type="button"
                                    onClick={cancelEpicEdit}
                                    disabled={savingEpic}
                                    className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                </form>
                            ) : (
                                <>
                                <div className="flex items-center gap-1.5">
                                <h3 className="font-semibold text-gray-800 uppercase text-sm">
                                {column.name}
                                </h3>
                                </div>
                                <div className="flex items-center gap-2">
                                {isTemplateMode && swimlane === sortedSwimlanes[0] && (
                                    <div className="relative" ref={epicDropdownId === column.epicId ? dropdownRef : null}>
                                    <Tooltip text="Column options">
                                        <FaCog
                                            className="w-3.5 h-3.5 text-gray-400 hover:text-primary-600 cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEpicDropdownId(epicDropdownId === column.epicId ? null : column.epicId);
                                            }}
                                            aria-label="Column options"
                                        />
                                    </Tooltip>
                                    {epicDropdownId === column.epicId && (
                                        <div className="absolute right-0 top-6 z-50 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEpicDropdownId(null);
                                                    startEpicEdit(column);
                                                }}
                                            >Rename</button>
                                            {/* D19 as decided: the estimate is set from
                                                THIS menu, not a second cog (Bill,
                                                2026-09-01: "I can't have two cogs"). */}
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEpicDropdownId(null);
                                                    setEffortEpicId(column.epicId);
                                                }}
                                            >Set effort…</button>
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEpicDropdownId(null);
                                                    setAddColumnName('');
                                                    setAddColumnPrompt({ referenceEpicId: column.epicId, position: 'left' });
                                                }}
                                            >Add Column Left</button>
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEpicDropdownId(null);
                                                    setAddColumnName('');
                                                    setAddColumnPrompt({ referenceEpicId: column.epicId, position: 'right' });
                                                }}
                                            >Add Column Right</button>
                                            <div className="border-t border-gray-200 my-1" />
                                            <button
                                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setEpicDropdownId(null);
                                                    setDeleteConfirm({ epicId: column.epicId, name: column.name });
                                                }}
                                            >Delete</button>
                                        </div>
                                    )}
                                    </div>
                                )}
                                {!isTemplateMode && isAdminPortal && swimlane.name === 'The Migration Experts Team' && (
                                    <Tooltip text="Rename column">
                                        <FaPencilAlt
                                            className="w-3 h-3 text-gray-400 hover:text-primary-600 cursor-pointer"
                                            onClick={(e) => { e.stopPropagation(); startEpicEdit(column); }}
                                            aria-label="Rename column"
                                        />
                                    </Tooltip>
                                )}
                                <span className="bg-gray-200 text-gray-700 text-xs font-medium px-2 py-1 rounded-full">
                                {columnTasks.length}
                                </span>
                                </div>
                                </>
                            )}
                            </div>
                            {/* T470 - the other header path. Same own-line
                                placement as above, same reason. The editor
                                opens on the FIRST lane only in template mode -
                                see renderEpicEffort's header. */}
                            {renderEpicEffort(column, {
                                // ⛔ THE FIRST NON-CLIENT LANE, NOT THE FIRST LANE.
                                // D3 returns null for the client lane's header, and
                                // sortedSwimlanes is ordered by `order` - so on a
                                // LEGACY Team-first add-in (ISS-023 left those
                                // unmigrated) the editor would be told to live in
                                // the one header that now renders nothing, and the
                                // estimate could not be set at all on that template.
                                editorHere: !isTemplateMode || swimlane === firstNonClientLane,
                                swimlane
                            })}
                            </div>

                            {/* Column Body */}
                            <div className="bg-gray-100 rounded-b-lg p-4 flex-1 space-y-3 overflow-y-auto">
                            {columnTasks.length === 0 ? (
                                <div className="text-center text-gray-400 text-sm py-8">
                                No tasks
                                </div>
                            ) : (
                                [...columnTasks].sort((a, b) => {
                                    if (isTemplateMode) {
                                        // Check for combinedMainTaskOrder (cross-template ordering)
                                        const storedOrder = project?.combinedMainTaskOrder;
                                        if (storedOrder && Object.keys(storedOrder).length > 0) {
                                            const epicOrder = storedOrder[a.epicId] || [];
                                            const aKey = `${a._sourceTemplateId || a.projectId}:${a.taskId}`;
                                            const bKey = `${b._sourceTemplateId || b.projectId}:${b.taskId}`;
                                            const aIndex = epicOrder.indexOf(aKey);
                                            const bIndex = epicOrder.indexOf(bKey);
                                            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
                                            if (aIndex !== -1) return -1;
                                            if (bIndex !== -1) return 1;
                                        }
                                        // Fall back to task.order field (same as Gantt)
                                        return (a.order || 0) - (b.order || 0);
                                    }
                                    // Sort by order field (set by admin Gantt reordering), then by earliest subtask start date
                                    const orderDiff = (a.order || 0) - (b.order || 0);
                                    if (orderDiff !== 0) return orderDiff;
                                    // Secondary sort: earliest subtask start date for tasks with same order
                                    const aSubtasks = tasks.filter(t => t.mainTaskId === a.taskId && t.projectId === a.projectId);
                                    const bSubtasks = tasks.filter(t => t.mainTaskId === b.taskId && t.projectId === b.projectId);
                                    const aStart = aSubtasks.map(st => st.startDate).filter(Boolean).sort()[0] || '9999-12-31';
                                    const bStart = bSubtasks.map(st => st.startDate).filter(Boolean).sort()[0] || '9999-12-31';
                                    return aStart.localeCompare(bStart);
                                }).map(task => {
                                    const isTeamTask = task.swimlaneId === teamSwimlaneId ||
                                    (task.swimlaneId === null && teamSwimlaneId === project?.default_swimlane);

                                    return (
                                        <div
                                        key={task._sourceTemplateId ? `${task._sourceTemplateId}:${task.taskId}` : task.taskId}
                                        draggable={(isTeamTask || isAdminPortal) && !readOnly}
                                        onDragStart={() => !readOnly && onDragStart(task)}
                                        {...((isTeamTask || isAdminPortal) ? cardHandlers(task) : {})}
                                        onClick={() => onTaskClick(isTemplateMode && task._sourceTemplateId ? `${task._sourceTemplateId}:${task.taskId}` : task.taskId)}
                                        onMouseEnter={() => setHoveredMainTaskId(task.taskId)}
                                        onMouseLeave={() => setHoveredMainTaskId(null)}
                                        className={`bg-white rounded-lg ${isCompact(swimlaneKey) ? 'p-2' : 'p-4'} transition-all cursor-pointer border ${
                                            // One border width for every card and every state -
                                            // any variation shifts the card by a pixel
                                            hoveredMainTaskId === task.taskId
                                            ? 'border-yellow-400 bg-yellow-50'
                                            : 'border-gray-200'
                                        }`}
                                        style={{
                                            boxShadow: cardBoxShadow(
                                                task._isAddin && task._isTeamSwimlane
                                                    ? `inset 3px 0 0 0 #3b82f6, inset -3px 0 0 0 ${task._addinColour}`
                                                    : task._isAddin
                                                    ? `inset 3px 0 0 0 ${task._addinColour}`
                                                    : isTeamTask
                                                    ? 'inset 3px 0 0 0 #3b82f6'
                                                    : null,
                                                hoveredMainTaskId === task.taskId
                                            )
                                        }}
                                        >
                                        {isCompact(swimlaneKey) ? (
                                            /* Compact view - just the title */
                                            <div className="flex items-center gap-2">
                                                {task._isAddin && (
                                                    <span
                                                        className="px-1 py-0.5 text-xs font-bold rounded flex-shrink-0"
                                                        style={{
                                                            backgroundColor: `${task._addinColour}20`,
                                                            color: task._addinColour
                                                        }}
                                                    >
                                                        {task._addinAbbreviation}
                                                    </span>
                                                )}
                                                <h4 className={`font-medium text-sm truncate flex-1 ${task.isClosed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                                    {task.isClosed && <FaCheck className="inline-block text-green-600 mr-1 flex-shrink-0" size={10} />}
                                                    {task.subject}
                                                </h4>
                                            </div>
                                        ) : (
                                            /* Detailed view - full content */
                                            <>
                                            {/* Lock icon for read-only tasks (not shown for admin portal) */}
                                            {!isTeamTask && !isAdminPortal && (
                                                <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                                </svg>
                                                <span>View only</span>
                                                </div>
                                            )}

                                            <div className="flex items-start justify-between mb-2">
                                            {/* Add-in badge */}
                                            {task._isAddin && (
                                                <span
                                                    className="px-1.5 py-0.5 text-xs font-bold rounded mr-2 flex-shrink-0"
                                                    style={{
                                                        backgroundColor: `${task._addinColour}20`,
                                                        color: task._addinColour
                                                    }}
                                                >
                                                    {task._addinAbbreviation}
                                                </span>
                                            )}
                                            <h4 className={`font-medium flex-1 pr-2 ${task.isClosed ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                                            {task.isClosed && <FaCheck className="inline-block text-green-600 mr-1 flex-shrink-0" size={11} />}
                                            {task.subject}
                                            </h4>
                                            <span className="text-xs text-gray-500">#{task.taskId}</span>
                                            </div>
                                            {task.description && (
                                                <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                                                {task.description}
                                                </p>
                                            )}
                                            {/* Effort badge (T472) - the SWIMLANE
                                                card path. This file renders cards
                                                in two places, exactly as it renders
                                                headers in two; a badge added to one
                                                is invisible in the other layout.
                                                NOT on the client's own lane (D1). */}
                                            {(isAdminPortal || isTemplateMode)
                                                && !isClientLaneTask(task, teamSwimlaneId, project)
                                                && effortLabel(
                                                task.effortEstimated, task.effortActual,
                                                { estimateOnly: isTemplateMode }
                                            ) && (
                                                <div className="mb-2">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 text-xs rounded bg-gray-100 ${
                                                        isOverrun(task.effortEstimated, task.effortActual)
                                                            ? 'text-red-600 font-semibold' : 'text-gray-700'
                                                    }`}
                                                    title="Estimated and actual effort, in days"
                                                >
                                                    {effortLabel(
                                                        task.effortEstimated, task.effortActual,
                                                        { estimateOnly: isTemplateMode }
                                                    )}
                                                </span>
                                                </div>
                                            )}
                                            {/* Date range on own row */}
                                            {(() => {
                                                const { start, end } = isTemplateMode
                                                    ? getEffectiveRelativeDateRange(task, tasks)
                                                    : getEffectiveDateRange(task, tasks);
                                                if (!start && !end) return null;
                                                return (
                                                    <div className="mb-2 flex items-center gap-2">
                                                    {start && (
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${isTemplateMode ? 'bg-gray-100 text-gray-700' : getStartDateBgColour(start)}`}>
                                                        <FaCalendarAlt className="w-3 h-3" />
                                                        {isTemplateMode ? formatRelativeDay(start) : formatDateUK(start)}
                                                        </span>
                                                    )}
                                                    {start && end && <span className={"text-xs text-gray-600"}>{'\u2192'}</span>}
                                                    {end && (
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${isTemplateMode ? 'bg-gray-100 text-gray-700' : getDueDateBgColour(end)}`}>
                                                        <FaCalendarAlt className="w-3 h-3" />
                                                        {isTemplateMode ? formatRelativeDay(end) : formatDateUK(end)}
                                                        </span>
                                                    )}
                                                    </div>
                                                );
                                            })()}
                                            <div className="flex items-start justify-between text-xs mt-2">
                                            {/* Assignees on the left - stacked vertically */}
                                            <div className="flex flex-col gap-1">
                                            {/* Second badge block - same rule as the
                                                first: no assignee, no name. */}
                                            {task.assignedUsersNames && task.assignedUsersNames.length > 0 ? (
                                                // Show all assignee names
                                                task.assignedUsersNames.map((name, idx) => (
                                                    <span key={idx} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs whitespace-nowrap">
                                                    {name}
                                                    </span>
                                                ))
                                            ) : null}
                                            </div>

                                            {/* Icons on the right */}
                                            <div className="flex items-center gap-2 flex-shrink-0">
                                            {/* Show comment count */}
                                            {(task.activeCommentsCount > 0 || (task.activeCommentsCount === undefined && task.totalComments > 0)) && (
                                                <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1">
                                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                                </svg>
                                                {task.activeCommentsCount !== undefined ? task.activeCommentsCount : task.totalComments}
                                                </span>
                                            )}

                                            {/* Show subtask count with "yours" indicator */}
                                            {(() => {
                                                const subtaskCount = tasks.filter(t => t.mainTaskId === task.taskId && t.projectId === task.projectId).length;
                                                if (subtaskCount === 0) return null;

                                                // Get current user ID for assignment check
                                                const userId = lead?.id;
                                                const userEmail = (lead?.email || '').toLowerCase();
                                                const matchesMe = (v) => v != null && (String(v) === String(userId) || (userEmail && String(v).toLowerCase() === userEmail));
                                                const mySubtasks = tasks.filter(t =>
                                                    String(t.mainTaskId) === String(task.taskId) &&
                                                    t.projectId === task.projectId &&
                                                    (matchesMe(t.assignedTo) ||
                                                    t.assignedUsers?.some(u => matchesMe(u)))
                                                ).length;

                                                return (
                                                    <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded flex items-center gap-1">
                                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                    </svg>
                                                    {subtaskCount}{mySubtasks > 0 && <span className="text-primary-600">({mySubtasks} yours)</span>}
                                                    </span>
                                                );
                                            })()}

                                            {/* Show blocked subtasks count */}
                                            {(() => {
                                                const subtasksForTask = tasks.filter(t => String(t.mainTaskId) === String(task.taskId) && t.projectId === task.projectId);
                                                const blockedSubtasks = subtasksForTask.filter(t => {
                                                    if (t.isClosed) return false;
                                                    return getBlockers(blockedTasksMap, t.taskId, t.projectId, isTemplateMode).length > 0;
                                                });
                                                if (blockedSubtasks.length === 0) return null;

                                                return (
                                                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded flex items-center gap-1">
                                                    {blockedSubtasks.length} blocked
                                                    </span>
                                                );
                                            })()}
                                            </div>
                                            </div>
                                            </>
                                        )}
                                        </div>
                                    );
                                })
                            )}

                            {/* Collapse button for Archived and Edge columns */}
                            {(column.name === 'Archived' || column.name.startsWith('Edge')) && (
                                <div className="text-center pt-2 border-t border-gray-300 mt-2">
                                <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const newCollapsed = new Set(collapsedColumns);
                                    newCollapsed.add(column.name);
                                    setCollapsedColumns(newCollapsed);
                                }}
                                className="text-xs text-gray-600 hover:text-gray-900 font-medium"
                                >
                                Collapse {COLLAPSE_ARROW}
                                </button>
                                </div>
                            )}
                            </div>
                            </>
                        )}
                        </div>
                    );
                })}
                </div>
                </div>
                )}
                </div>
            );
        })}
        </div>

        {/* Add Column Prompt */}
        {isTemplateMode && addColumnPrompt && (
            <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center" onClick={() => { setAddColumnPrompt(null); setAddColumnName(''); }}>
                <div className="bg-white rounded-lg shadow-xl p-6 w-80" onClick={(e) => e.stopPropagation()}>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">
                        Add Column {addColumnPrompt.position === 'left' ? 'Left' : 'Right'}
                    </h4>
                    <input
                        type="text"
                        value={addColumnName}
                        onChange={(e) => setAddColumnName(e.target.value)}
                        placeholder="Column name"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3"
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Escape') { setAddColumnPrompt(null); setAddColumnName(''); }
                            if (e.key === 'Enter' && addColumnName.trim()) handleAddColumn();
                        }}
                        disabled={epicActionLoading}
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => { setAddColumnPrompt(null); setAddColumnName(''); }}
                            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                            disabled={epicActionLoading}
                        >Cancel</button>
                        <button
                            onClick={handleAddColumn}
                            className="px-3 py-1.5 text-sm text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                            disabled={!addColumnName.trim() || epicActionLoading}
                        >{epicActionLoading ? 'Adding...' : 'Add'}</button>
                    </div>
                </div>
            </div>
        )}

        {/* Delete Column Confirmation */}
        {isTemplateMode && deleteConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center" onClick={() => setDeleteConfirm(null)}>
                <div className="bg-white rounded-lg shadow-xl p-6 w-96" onClick={(e) => e.stopPropagation()}>
                    <h4 className="text-sm font-semibold text-gray-800 mb-3">Delete Column</h4>
                    <p className="text-sm text-gray-600 mb-4">
                        Are you sure you want to delete the column &apos;{deleteConfirm.name}&apos;?
                    </p>
                    <div className="flex justify-end gap-2">
                        <button
                            onClick={() => setDeleteConfirm(null)}
                            className="px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
                            disabled={epicActionLoading}
                        >Cancel</button>
                        <button
                            onClick={handleDeleteColumn}
                            className="px-3 py-1.5 text-sm text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                            disabled={epicActionLoading}
                        >{epicActionLoading ? 'Deleting...' : 'Delete'}</button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default KanbanBoard;
